import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useOnboarding } from "@/context/OnboardingContext";
import { useSettings } from "@/context/SettingsContext";
import { useSettingsState } from "@/context/SettingsStateContext";
import { useWebsites } from "@/context/WebsitesContext";
import { useWordPress } from "@/context/WordPressContext";
import useWordpressSettings from "@/hooks/useWordpressSettings";
import { toast } from "sonner";

export default function PublishingPreferencesStep() {
  const { state, setState } = useOnboarding();
  const {
    postingFrequency,
    writingStyle,
    subjectMatters,
    formattemplate,
    imagePrompt,
    imageModel,
    negativePrompt,
    weeklyPlanningDay,
    setWeeklyPlanningDay,
    contentPolicies,
    updateSettingsInDatabase,
  } = useSettings();
  const { currentWebsite } = useWebsites();
  const { wpPublishStatus, directWpSettings } = useSettingsState();
  const { settings: wpSettings } = useWordPress();
  const { updateWordPressPublishStatus } = useWordpressSettings();

  const [publishToWordPress, setPublishToWordPress] = useState<boolean>(
    wpPublishStatus === "publish"
  );
  const [localWeeklyPlanningDay, setLocalWeeklyPlanningDay] = useState<string>(
    weeklyPlanningDay || "friday"
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Check if WordPress is already connected
  const isWordPressConnected = wpSettings?.is_connected || directWpSettings?.is_connected;

  useEffect(() => {
    setPublishToWordPress(wpPublishStatus === "publish");
  }, [wpPublishStatus]);

  const handleContinue = async () => {
    try {
      setIsSaving(true);

      if (!currentWebsite) {
        toast.error("Please select a website first");
        return;
      }

      // Persist WordPress publish preference when possible
      try {
        await updateWordPressPublishStatus(
          publishToWordPress ? "publish" : "draft"
        );
      } catch (e) {
        console.warn("Could not update WordPress publish status now", e);
      }

      // Persist weekly planning day using existing settings flow
      try {
        setWeeklyPlanningDay(localWeeklyPlanningDay);
        await updateSettingsInDatabase(
          postingFrequency,
          writingStyle,
          subjectMatters,
          formattemplate,
          imagePrompt,
          imageModel,
          negativePrompt,
          localWeeklyPlanningDay,
          contentPolicies
        );
      } catch (e) {
        console.error("Failed saving weekly planning day", e);
        toast.error("Failed to save planning day");
      }

      // Proceed to completion
      setState((prev) => ({ ...prev, step: 7 }));
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("step", "integration");
      window.history.replaceState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      key="step-5"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, ease: "easeInOut", opacity: { duration: 0.3 } }}
      className="w-full py-4"
    >
      <div className="w-full mx-auto max-w-3xl">
        <h2 className="text-2xl font-bold mb-4 text-center">Set your publishing preferences</h2>
        <p className="text-muted-foreground text-center mb-8">
          Choose how content should be handled and when we plan your week.
        </p>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Publishing preference</CardTitle>
            <CardDescription>
              {isWordPressConnected 
                ? "WordPress is connected! Choose how to publish your content."
                : "Publish directly to WordPress or save as drafts only."
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isWordPressConnected ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div>
                      <p className="font-medium text-green-800">Connected to WordPress</p>
                      <p className="text-sm text-green-600">
                        {wpSettings?.wp_url || directWpSettings?.wp_url}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Publish in WordPress</Label>
                    <p className="text-sm text-muted-foreground">
                      Turn off to keep everything as drafts.
                    </p>
                  </div>
                  <Switch
                    checked={publishToWordPress}
                    onCheckedChange={setPublishToWordPress}
                    className="capitalize"
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Publish in WordPress</Label>
                  <p className="text-sm text-muted-foreground">
                    Turn off to keep everything as drafts.
                  </p>
                </div>
                <Switch
                  checked={publishToWordPress}
                  onCheckedChange={setPublishToWordPress}
                  className="capitalize"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Weekly planning day</CardTitle>
            <CardDescription>
              Pick the main day for your weekly content planning.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Label>Planning day</Label>
              <Select
                value={localWeeklyPlanningDay}
                onValueChange={setLocalWeeklyPlanningDay}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select planning day" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monday">Monday</SelectItem>
                  <SelectItem value="tuesday">Tuesday</SelectItem>
                  <SelectItem value="wednesday">Wednesday</SelectItem>
                  <SelectItem value="thursday">Thursday</SelectItem>
                  <SelectItem value="friday">Friday</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => {
              setState((prev) => ({ ...prev, step: 7 }));
              const searchParams = new URLSearchParams(window.location.search);
              searchParams.set("step", "integration");
              window.history.replaceState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
            }}
          >
            Skip
          </Button>
          <Button onClick={handleContinue} disabled={isSaving}>
            {isSaving ? "Saving…" : "Continue"}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}


